/**
 * エピソードダウンロードボタン
 *
 * 個別エピソードのオフラインダウンロード / 削除 / ステータス表示を行う小さなコンポーネント
 */
import { Clock, CloudOff, Download, Loader2, Pause, RotateCw, Settings2, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { EpisodeList } from '@/api/type'
import { CompactDownloadBadge } from '@/components/shared/compact-download-badge'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useEpisodeDownload } from '@/hooks/useEpisodeDownload'
import { discardPendingDownload, getOfflineEpisodeSize, getPendingDownload } from '@/lib/db'
import { formatBytes } from '@/lib/format-utils'
import { cn } from '@/lib/utils'
import { prepareVideoVariant } from '@/lib/video-variant'
import { type DownloadStatus, useDownloadStore } from '@/store/useDownloadStore'

import { DownloadQualityDialog } from './download-quality-dialog'

type Props = {
  episode: EpisodeList
  productId: number
  domain: string
  /** コンパクトモード（グリッド内のアイコンのみ表示） */
  compact?: boolean
}

export const EpisodeDownloadButton = ({ episode, productId, domain, compact = false }: Props) => {
  const { downloadEpisode, removeOfflineEpisode, abort } = useEpisodeDownload()
  const downloadInfo = useDownloadStore((s) => s.downloads.get(episode.episode_id))
  // ダウンロード済みファイルのサイズ（未保存なら null。保存済み判定も兼ねる）
  const [offlineSize, setOfflineSize] = useState<number | null>(null)
  // 中断したままのダウンロードの進捗率（未中断なら null）
  const [pendingPercent, setPendingPercent] = useState<number | null>(null)
  // 中断中ダウンロードの「再開 / 削除」選択ダイアログ
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false)
  // 画質選択ダイアログ
  const [qualityDialogOpen, setQualityDialogOpen] = useState(false)
  // 選択した画質の変換進捗（変換していなければ null）
  const [conversionPercent, setConversionPercent] = useState<number | null>(null)
  // 変換ポーリングの中断用
  const conversionAbortRef = useRef<AbortController | null>(null)
  // 中断中ダウンロードの画質（再開時は同じ画質で続きから取得する）
  const [pendingQuality, setPendingQuality] = useState<string | null>(null)
  const isVideo = (episode.mime_type ?? '').startsWith('video/')

  // アンマウント時に変換ポーリングを止める
  useEffect(() => () => conversionAbortRef.current?.abort(), [])

  // オフライン状態と中断中ダウンロードの進捗をまとめて取得
  useEffect(() => {
    let cancelled = false
    Promise.all([getOfflineEpisodeSize(episode.episode_id), getPendingDownload(episode.episode_id)])
      .then(([size, pending]) => {
        if (cancelled) return
        setOfflineSize(size)
        setPendingQuality(pending?.quality ?? null)
        // 100% に達している場合は「続きから」ではなく再取得になるため中断中扱いにしない
        const percent =
          pending && pending.totalBytes > 0 ? Math.floor((pending.receivedBytes / pending.totalBytes) * 100) : null
        setPendingPercent(percent !== null && percent < 100 ? percent : null)
      })
      .catch(() => {
        if (cancelled) return
        setOfflineSize(null)
        setPendingQuality(null)
        setPendingPercent(null)
      })
    return () => {
      cancelled = true
    }
  }, [episode.episode_id, downloadInfo?.status])

  /** 実際のダウンロード開始（画質確定後） */
  const startDownload = useCallback(
    (quality: string) => {
      downloadEpisode({
        domain,
        episodeId: episode.episode_id,
        mimeType: episode.mime_type ?? 'application/octet-stream',
        productId,
        quality,
        title: episode.custom_name ?? `Episode ${episode.index}`,
      })
    },
    [downloadEpisode, episode, productId, domain],
  )

  /**
   * 画質選択後の処理。
   * 未生成の画質はここで変換完了を待ち、進捗はダウンロードボタン上に % で表示する。
   */
  const handleQualitySelect = useCallback(
    async (quality: string) => {
      if (quality === 'original') {
        startDownload(quality)
        return
      }

      const controller = new AbortController()
      conversionAbortRef.current?.abort()
      conversionAbortRef.current = controller
      setConversionPercent(0)

      const available = await prepareVideoVariant(episode.episode_id, quality, {
        onProgress: (progress) => {
          if (!controller.signal.aborted) setConversionPercent(progress)
        },
        signal: controller.signal,
      })

      if (controller.signal.aborted) return
      conversionAbortRef.current = null
      setConversionPercent(null)
      if (available) startDownload(quality)
    },
    [episode.episode_id, startDownload],
  )

  /** 変換待ちの中止 */
  const handleCancelConversion = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    conversionAbortRef.current?.abort()
    conversionAbortRef.current = null
    setConversionPercent(null)
  }, [])

  const handleDownload = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      // 動画は画質を選ばせる。それ以外は原本をそのまま取得する
      if (isVideo) {
        setQualityDialogOpen(true)
        return
      }
      startDownload('original')
    },
    [isVideo, startDownload],
  )

  /** 中断中バッジのクリック：すぐに再開せず、再開か削除かを選ばせる */
  const handleOpenResumeDialog = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setResumeDialogOpen(true)
  }, [])

  /** ダイアログから「続きから再開」：中断時と同じ画質で再開する */
  const handleResume = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setResumeDialogOpen(false)
      startDownload(pendingQuality ?? 'original')
    },
    [pendingQuality, startDownload],
  )

  /** ダイアログから「中断データを削除」 */
  const handleDiscard = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      setResumeDialogOpen(false)
      try {
        await discardPendingDownload(episode.episode_id)
        setPendingPercent(null)
        toast.success('中断データを削除しました')
      } catch (error) {
        console.error('[Download] 中断データの削除に失敗しました:', error)
        toast.error('中断データの削除に失敗しました')
      }
    },
    [episode.episode_id],
  )

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      removeOfflineEpisode(episode.episode_id).then(() => setOfflineSize(null))
    },
    [removeOfflineEpisode, episode.episode_id],
  )

  /** 進行中ダウンロードの一時停止（中間データは残るので続きから再開できる） */
  const handlePause = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      abort(episode.episode_id)
    },
    [abort, episode.episode_id],
  )

  const status: DownloadStatus = downloadInfo?.status ?? (offlineSize !== null ? 'done' : 'idle')
  // ダウンロードを行っていないときだけ「中断中」として扱う
  const resumePercent = status === 'idle' || status === 'error' ? pendingPercent : null

  return (
    <>
      {compact ? (
        <CompactDownloadBadge
          status={status}
          progress={downloadInfo?.progress}
          resumePercent={resumePercent}
          conversionPercent={conversionPercent}
          downloadedSize={offlineSize}
          onDownload={handleDownload}
          onRemove={handleRemove}
          onPause={handlePause}
          onResume={handleOpenResumeDialog}
          onCancelConversion={handleCancelConversion}
        />
      ) : (
        <FullButton
          status={status}
          progress={downloadInfo?.progress}
          resumePercent={resumePercent}
          conversionPercent={conversionPercent}
          downloadedSize={offlineSize}
          onDownload={handleDownload}
          onRemove={handleRemove}
          onPause={handlePause}
          onResume={handleOpenResumeDialog}
          onCancelConversion={handleCancelConversion}
        />
      )}

      <AlertDialog open={resumeDialogOpen} onOpenChange={setResumeDialogOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>中断したダウンロードがあります</AlertDialogTitle>
            <AlertDialogDescription>
              {resumePercent ?? 0}%
              まで受信済みです。続きから再開するか、中断データを削除して空き容量を戻すかを選択してください。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>キャンセル</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDiscard}>
              <Trash2 size={16} />
              削除
            </Button>
            <Button onClick={handleResume}>
              <RotateCw size={16} />
              続きから再開
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isVideo && (
        <DownloadQualityDialog
          episodeId={episode.episode_id}
          open={qualityDialogOpen}
          onOpenChange={setQualityDialogOpen}
          onConfirm={handleQualitySelect}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// サブコンポーネント
// ---------------------------------------------------------------------------

const FullButton = ({
  status,
  progress,
  resumePercent,
  conversionPercent,
  downloadedSize,
  onDownload,
  onRemove,
  onPause,
  onResume,
  onCancelConversion,
}: {
  status: DownloadStatus
  progress?: number
  resumePercent: number | null
  /** 画質変換の進捗率（変換していなければ null） */
  conversionPercent: number | null
  /** ダウンロード済みファイルのサイズ（未保存なら null） */
  downloadedSize: number | null
  onDownload: (e: React.MouseEvent) => void
  onRemove: (e: React.MouseEvent) => void
  onPause: (e: React.MouseEvent) => void
  onResume: (e: React.MouseEvent) => void
  onCancelConversion: (e: React.MouseEvent) => void
}) => {
  // 変換待ち：ダウンロード開始前の準備段階なので最優先で表示する
  if (conversionPercent !== null) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-auto px-1.5 gap-0.5 text-violet-400 hover:text-red-400 hover:bg-zinc-700 group"
        onClick={onCancelConversion}
        title={`画質を変換中 ${conversionPercent}%（クリックで中止）`}
      >
        <Settings2 size={16} className="animate-spin group-hover:hidden" />
        <X size={16} className="hidden group-hover:block" />
        <span className="text-[10px] font-bold leading-none">{conversionPercent}%</span>
      </Button>
    )
  }

  if (status === 'done') {
    const sizeLabel = downloadedSize !== null ? formatBytes(downloadedSize) : null
    return (
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 text-green-400 hover:text-red-400 hover:bg-zinc-700',
          sizeLabel && 'w-auto gap-0.5 px-1.5',
        )}
        onClick={onRemove}
        title={sizeLabel ? `オフライン保存済み ${sizeLabel}（クリックで削除）` : 'オフライン保存済み（クリックで削除）'}
      >
        <CloudOff size={16} />
        {sizeLabel && <span className="text-[10px] font-bold leading-none whitespace-nowrap">{sizeLabel}</span>}
      </Button>
    )
  }

  if (status === 'queued') {
    // 直列キューで順番待ち。クリックでキューから外せる
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-zinc-400 hover:text-amber-400 hover:bg-zinc-700 group"
        onClick={onPause}
        title="ダウンロード待ち（クリックで取り消し）"
      >
        <Clock size={16} className="group-hover:hidden" />
        <X size={16} className="hidden group-hover:block" />
      </Button>
    )
  }

  if (status === 'downloading' || status === 'saving') {
    // disabled にすると pointer-events が無効になりクリックが背後の要素へ抜けるため、
    // 押せる状態のまま「一時停止」を担わせる
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-blue-400 hover:text-amber-400 hover:bg-zinc-700 group"
        onClick={onPause}
        title={`ダウンロード中 ${progress ?? 0}%（クリックで一時停止）`}
      >
        <Loader2 size={16} className="animate-spin group-hover:hidden" />
        <Pause size={16} className="hidden group-hover:block" />
      </Button>
    )
  }

  // 中断中：再開位置を % で示し、アイコンをオレンジにする
  if (resumePercent !== null) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-auto px-1.5 gap-0.5 text-amber-400 hover:text-amber-300 hover:bg-zinc-700"
        onClick={onResume}
        title={`中断中 ${resumePercent}%（クリックで再開または削除）`}
      >
        <RotateCw size={16} />
        <span className="text-[10px] font-bold leading-none">{resumePercent}%</span>
      </Button>
    )
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-700"
      onClick={onDownload}
      title="オフラインにダウンロード"
    >
      <Download size={16} />
    </Button>
  )
}
